import axios from 'axios';
import axiosRetry from 'axios-retry';
import pako from 'pako';

import {
    UserSessionRequest,
    EmptyFileApiResponse,
    FileApiResponse,
    CreateFileRequest,
    FileSessionId,
    FileId,
    FileFragmentId,
    FileSessionVM,
    CreateFileFragmentRequest,
    PublicEncryptionKey,
    EncryptedSymmetricKey,
    UserSessionVM,
    FileVM,
    FileFragmentVM
} from '../FileAPIClient/Api.types';

import {
    decryptSymmetricKeyAndIV,
    KeyData,
    calculateUploadChecksum,
    calculateHash,
    blobAsArrayBuffer,
    encrypt,
    generateRSAKeyPair,
    exportPublicKey,
    EncryptionInfo
} from './Cryptography';
import { FileApiSessionStorage } from './FileApiSessionStorage';

/**
 * Underlättar integrering mot FileAPI (INUT-data)
 * Erbjuder typade metoder för API anrop.
 * 
 * Hanterar krypteringen kring att hämta symmetriska krypteringsnycklar.
 * Hanterar kryptering av uppladdade filer.
 */
export class FileAPIClient {
    private apiBaseUrl: string = ''; //Relative path
    private minutApiBase: string = 'minut/api/v1.0/';
    private authenticationChanged?: (isAuthenticated: boolean) => void;

    private DEFAULT_API_CONFIG = {
        withCredentials: true,
        headers: {
            Accept: 'application/json',
        },
    };

    private isSessionValid: boolean = false;
    private storage: FileApiSessionStorage;

    constructor(authenticationChanged?: (isAuthenticated: boolean) => void) {
        this.storage = new FileApiSessionStorage();
        this.authenticationChanged = authenticationChanged;
        axiosRetry(axios, {
            retries: 5,
            retryDelay: () => {
                return 500;
            },
            retryCondition: (error) => {
                return axiosRetry.isNetworkOrIdempotentRequestError(error);
            },
            shouldResetTimeout: true,
            onRetry: (retryCount: number, error) => {
                console.log('Försök nummer ' + retryCount + ' Message: ' + error.message);
            },
        });
    }

    getIsAuthenticated(): boolean {
        return this.isSessionValid;
    }

    private setAuthenticated(isAuthenticated: boolean): void {
        if (this.isSessionValid !== isAuthenticated) {
            this.isSessionValid = isAuthenticated;
            if (this.authenticationChanged) {
                this.authenticationChanged(isAuthenticated);
            }
        }
    }

    private async getOrThrow<T>(url: string): Promise<T> {

        if (!this.isSessionValid) {
            throw new Error('Du har blivit utloggad');
        }

        try {
            const response = await axios.get<T>(url, this.DEFAULT_API_CONFIG);

            const result = {
                success: response.status >= 200 && response.status < 300,
                code: response.status,
                data: response.data
            };
            return result.data;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const r = error.response;
                if (r.status === 403) {
                    this.setAuthenticated(false);
                    this.storage.clearPersistedState();
                    throw new Error('Du har blivit utloggad');
                }
                if (r.status === 404) {
                    throw new Error('Resursen hittades inte');
                }
                if (r.status === 408) {
                    throw new Error('Anropet tog för lång tid att slutföra');
                }
                if (r.status === 500) {
                    throw new Error('Serverfel uppstod vid anropet');
                }
                throw new Error(`Ett oväntat fel uppstod med statuskod: ${r.status}`);
            }
            throw error;
        }
    }

    private async get<T>(url: string): Promise<FileApiResponse<T>> {

        if (!this.isSessionValid) {
            throw new Error('Du har blivit utloggad');
        }

        try {
            const response = await axios.get<T>(url, this.DEFAULT_API_CONFIG);

            return {
                success: response.status >= 200 && response.status < 300,
                code: response.status,
                data: response.data
            };
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const r = error.response;
                if (r.status === 403) {
                    this.setAuthenticated(false);
                    this.storage.clearPersistedState();
                }

                return {
                    success: r.status >= 200 && r.status < 300,
                    code: r.status,
                    data: r.data
                };
            }
            throw error;
        }
    }

    private async post<T>(url: string, data: any): Promise<FileApiResponse<T>> {

        if (!this.isSessionValid) {
            throw new Error('Du har blivit utloggad');
        }

        const response = await axios.post<T>(url, data, this.DEFAULT_API_CONFIG);
        if (response.status === 403) {
            this.setAuthenticated(false);
            this.storage.clearPersistedState();
        }
        return {
            success: response.status >= 200 && response.status < 300,
            code: response.status,
            data: response.data
        };
    }

    /*******************
     * User API calls
     * *****************/
    async getUserSession(token: string | undefined): Promise<FileApiResponse<UserSessionVM>> {
        const url = `${this.apiBaseUrl}${this.minutApiBase}UserSession/Request`;
        const body: UserSessionRequest = { token: token };

        const response = await axios.post<UserSessionVM>(url, body, this.DEFAULT_API_CONFIG);
        if (response.status === 403) {
            this.setAuthenticated(false);
            this.storage.clearPersistedState();
        }
        var apiResponse = {
            success: response.status >= 200 && response.status < 300,
            code: response.status,
            data: response.data
        };
        if (apiResponse.success && apiResponse.data) {
            this.setAuthenticated(response.data.isAuthenticated);
        }
        else {
            this.setAuthenticated(false);
            this.storage.clearPersistedState();
        }

        this.setAuthenticated(true);
        return apiResponse;
    }

    async isAuthenticated(): Promise<boolean> {
        if (this.isSessionValid) {
            return true;
        }

        const isValid = await this.validateSession();
        this.setAuthenticated(isValid);
        return this.isSessionValid;
    }

    async refreshUserSession(): Promise<void> {
        const url = `${this.apiBaseUrl}${this.minutApiBase}UserSession/Refresh`;

        await axios.put(url, this.DEFAULT_API_CONFIG);
    }

    private async validateSession(): Promise<boolean> {
        const url = `${this.apiBaseUrl}${this.minutApiBase}UserSession/Validate`;

        const response = await axios.get<boolean>(url, this.DEFAULT_API_CONFIG);
        if (response.status === 403) {
            this.setAuthenticated(false);
            this.storage.clearPersistedState();
        }
        return response.status >= 200 && response.status < 300
    }

    /*******************
     * FileSession API calls
     * *****************/
    async getFileSessionsForUser(): Promise<FileApiResponse<FileSessionVM[]>> {
        const url = `${this.apiBaseUrl}${this.minutApiBase}FileSession/`;
        return await this.get<FileSessionVM[]>(url);
    }

    async getFileSession(fileSessionId: FileSessionId): Promise<FileSessionVM> {
        const url = `${this.apiBaseUrl}${this.minutApiBase}FileSession/${fileSessionId.value}`;
        return await this.getOrThrow<FileSessionVM>(url)
    }

    async requestSymmetricKey(fileSessionId: FileSessionId) {
        // Check if we have stored data for this file session
        const reference = this.storage.getSymmetricKeyReference(fileSessionId);

        if (reference) {
            // If we have a valid symmetric key reference for this session, no need to request new one
            return;
        }

        var cryptoKeyPair = await generateRSAKeyPair();
        const pemExported = await exportPublicKey(cryptoKeyPair);

        const requestBody: PublicEncryptionKey = {
            value: pemExported,
        };

        const url = `${this.apiBaseUrl}${this.minutApiBase}FileSession/${fileSessionId.value}/RequestSymmetricKey`;
        const response = await this.post<string>(url, requestBody);

        if (!response.data) {
            throw new Error('Misslyckades att begära symmetriska nycklar');
        }
        this.storage.saveCryptoKeyValues(fileSessionId, response.data, cryptoKeyPair);
    }

    async getSymmetricKey(fileSessionId: FileSessionId): Promise<KeyData | null> {
        if (!this.storage.getSymmetricKeyReference(fileSessionId)) {
            await this.requestSymmetricKey(fileSessionId)
        }

        var symmetricKeyReference = this.storage.getSymmetricKeyReference(fileSessionId);
        if (!symmetricKeyReference) {
            throw new Error('Ingen referens till symmetrisk nyckel hittades');
        }

        const url = `${this.apiBaseUrl}${this.minutApiBase}FileSession/${fileSessionId.value}/SymmetricKey/${symmetricKeyReference}`;

        try {
            const response = await axios.get<EncryptedSymmetricKey>(url, this.DEFAULT_API_CONFIG);

            if (response.status === 202) {
                return null;
            }
            if (response.status === 200) {
                const symKey = response.data;
                const cryptoKeyPair = await this.storage.getCryptoKeyPair(fileSessionId);
                const key: KeyData = await decryptSymmetricKeyAndIV(
                    symKey.key,
                    symKey.iv,
                    cryptoKeyPair
                );

                return key;
            }
            throw new Error(`Ett oväntat fel uppstod med statuskod: ${response.status}`);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                this.storage.clearDataForSession(fileSessionId);
                throw new Error(`Misslyckades att hämta symmetrisk nyckel: ${error.response.statusText}`);
            }
            throw error;
        }
    }

    async fileSessionDone(fileSessionId: FileSessionId): Promise<EmptyFileApiResponse> {
        const url = `${this.apiBaseUrl}${this.minutApiBase}FileSession/${fileSessionId.value}/Done`;

        try {
            const response = await axios.put(url, null);
            return {
                success: true,
                code: response.status,
            };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 401 || error.code === 'ECONNRESET' || error.request) {
                    return {
                        success: false,
                        code: error.response?.status || 500,
                    };
                }
            }
        }
        return {
            success: false,
            code: 500,
        };
    }

    /*******************
     * File API calls
     * *****************/

    async createFile(fileSessionId: FileSessionId, name: string, size: number, checksum: string): Promise<FileVM> {
        const url = `${this.apiBaseUrl}${this.minutApiBase}FileSession/${fileSessionId.value}/NewFile`;

        const content: CreateFileRequest = {
            fileName: name,
            fileSize: size,
            checksum: checksum
        };
        const response = await axios.post<FileVM>(url, content);

        return response.data;
    };

    async getFile(fileId: FileId): Promise<FileVM> {
        const url = `${this.apiBaseUrl}${this.minutApiBase}File/${fileId.value}`;
        const response = await this.getOrThrow<FileVM>(url);

        return response;
    };

    async getFileFragmentInfos(fileId: FileId): Promise<FileFragmentVM[]> {
        const url = `${this.apiBaseUrl}${this.minutApiBase}File/${fileId.value}/FileFragments`;
        const response = await this.getOrThrow<FileFragmentVM[]>(url);

        return response;
    };

    async uploadFileFragment(
        fileId: FileId,
        fileBlob: Blob,
        fileFragmentIndex: number,
        otherPartyPublicKey: string) {
        const checksum = await calculateUploadChecksum(fileBlob);
        const hash = await calculateHash(fileBlob);
        const bufferedFragmentData: ArrayBuffer = await blobAsArrayBuffer(fileBlob);
        const compressedFragmentData = pako.gzip(bufferedFragmentData);
        if (compressedFragmentData === undefined) {
            throw new Error('Misslyckades att komprimera filen');
        }
        const encryptionInfo: EncryptionInfo = await encrypt(compressedFragmentData, otherPartyPublicKey, fileFragmentIndex, hash);

        const request: CreateFileFragmentRequest = {
            fileFragmentIndex: fileFragmentIndex,
            size: fileBlob.size,
            checksum: checksum,
            publicKey: encryptionInfo.publicKey,
            nonce: encryptionInfo.nonce,
            associatedData: encryptionInfo.associatedData,
            ciphertext: encryptionInfo.ciphertext,
        };

        const url = `${this.apiBaseUrl}${this.minutApiBase}File/${fileId.value}/UploadFileFragment`;
        await axios.post<FileFragmentId>(url, request, this.DEFAULT_API_CONFIG);
    };

    /**
     * Laddar ner fragmentdata
     * @returns {byte[]} Datastream.
     */
    async downloadFileFragment(
        fileId: FileId,
        index: number) {

        const url = `${this.apiBaseUrl}${this.minutApiBase}File/${fileId.value}/DownloadFileFragment/${index}`;

        try {
            const response = await axios.get(url, this.DEFAULT_API_CONFIG);
            return response;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const status = error.response.status;
                if (status === 401 || status === 403) {
                    this.setAuthenticated(false);
                    this.storage.clearPersistedState();
                    throw new Error('Du har blivit utloggad');
                }
            }
            throw error;
        }
    };

    async abortFileDownload(fileId: FileId) {
        const url = `${this.apiBaseUrl}${this.minutApiBase}File/${fileId.value}/AbortDownload`;
        const response = await axios.put(url, null, this.DEFAULT_API_CONFIG);
    };

    async deleteFile(fileId: FileId): Promise<FileId> {
        const url = `${this.apiBaseUrl}${this.minutApiBase}File/${fileId.value}`;
        const response = await axios.delete<FileId>(url, this.DEFAULT_API_CONFIG);
        return response.data;
    };

    async fileTransferDone(fileId: FileId): Promise<void> {
        const url = `${this.apiBaseUrl}${this.minutApiBase}File/${fileId.value}/Done`;
        await axios.put(url, null, this.DEFAULT_API_CONFIG);
    };
}